import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonColors, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import LeadPhoneCall, {
  leadPhoneCallColors,
  leadPhoneCallDesigns,
  leadPhoneCallSizes,
  leadPhoneCallTarget
} from '@adv-mt/mt-lead-phone-call'
import IconChatOutline from '@adv-mt/mt-svg-icons/lib/ChatOutline'
import IconMail from '@adv-mt/mt-svg-icons/lib/MailOutline'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

const ContactOptions = ({ad = {}, defaultMessage, extendedLeadTrackingData = {}, onClickContact}) => {
  const {i18n, domain, user} = useContext(Context)
  const {isLogged: isUserLogged} = useContext(AuthenticationContext)
  const {push: routerPush} = useRouter()
  const [isNavigating, setIsNavigating] = useState(false)
  const [leadData, setLeadData] = useState()
  const {track} = useAnalyticsTrack()

  const config = domain.get('config')
  const {LIST_TRIGGERED_EVENT, LEAD_TYPES, AUTHENTICATION_HIT_INFORMATIONS_2} = config.get('tracking')

  useMount(async () => {
    const [, _leadData] = await domain.get('get_lead_data_tracking_use_case').execute({
      ad: {...ad, id: ad?.adId},
      dealerId: ad?.contractId
    })
    setLeadData(_leadData)
  })

  const contactExtendedTrackingData = {
    ...extendedLeadTrackingData,
    page_name_origin: 'assistant',
    page_type: 'form'
  }

  if (!ad?.adId) {
    return null
  }

  const handleSendMessage = async () => {
    setIsNavigating(true)
    const [error, response] = await domain.get('get_messaging_conversation_id_user_use_case').execute({
      entityId: ad.adId,
      buyerId: user?.userId,
      sellerId: ad.contractId ?? ad.sellerId,
      isProfessionalSeller: ad.isProfessional
    })
    setIsNavigating(false)

    if (error || !response?.conversationId) {
      routerPush({pathname: '/user/messages/'})
      return
    }

    track({
      event: LIST_TRIGGERED_EVENT,
      data: {
        ...leadData,
        ...contactExtendedTrackingData,
        lead_type: LEAD_TYPES.NET_AD_REPLIED_CHAT,
        hit_information: response.conversationId
      }
    })

    routerPush({
      pathname: '/user/messages/',
      hash: `#/id/${response.conversationId}`,
      state: {defaultMessage, adId: ad.adId, sellerId: ad.contractId, isProfessionalSeller: ad.isProfessional}
    })
  }

  return (
    <>
      {!isUserLogged && (
        <AtomButton
          fullWidth
          color={atomButtonColors.ACCENT}
          shape={atomButtonShapes.CIRCULAR}
          size={atomButtonSizes.LARGE}
          leftIcon={<IconMail />}
          onClick={() => onClickContact(ad, {...leadData, ...contactExtendedTrackingData})}
        >
          {i18n.t('COMMON_CONTACT')}
        </AtomButton>
      )}
      {isUserLogged && (
        <AtomButton
          fullWidth
          color={atomButtonColors.ACCENT}
          shape={atomButtonShapes.CIRCULAR}
          size={atomButtonSizes.LARGE}
          leftIcon={<IconChatOutline />}
          isLoading={isNavigating}
          disabled={isNavigating}
          onClick={handleSendMessage}
        >
          {i18n.t('NET.CONTACT_OPTIONS.SEND_MESSAGE')}
        </AtomButton>
      )}
      {!ad?.hidePhone && (
        <LeadPhoneCall
          adId={ad.adId}
          authenticationHitInformation2={AUTHENTICATION_HIT_INFORMATIONS_2.NET}
          contractId={ad.contractId}
          isProfessionalDealer={ad.isProfessional}
          color={leadPhoneCallColors.PRIMARY}
          design={leadPhoneCallDesigns.OUTLINE}
          size={leadPhoneCallSizes.large}
          target={leadPhoneCallTarget.netAdDetail}
          extendedLeadTrackingData={{...leadData, ...extendedLeadTrackingData}}
          isFullWidth
        />
      )}
    </>
  )
}

ContactOptions.propTypes = {
  ad: PropTypes.object.isRequired,
  defaultMessage: PropTypes.string,
  extendedLeadTrackingData: PropTypes.object,
  onClickContact: PropTypes.func
}

export default ContactOptions
